import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
    Boat, Buildings, Calendar, Clock, Package, Play, Plus, Truck, User, WarningOctagon, ArrowRight,
} from "@phosphor-icons/react";
import StartExportModal from "./StartExportModal";
import LoadOutStation from "./LoadOutStation";

const NUM = (n, d = 2) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: d });

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

function fmtETA(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = d.getTime() - now.getTime();
        const diffH = Math.abs(diffMs) / 3600000;
        const rel = diffMs > 0 ? "in" : "ago";
        const label = diffH < 1 ? `${Math.round(diffH * 60)}m` : diffH < 24 ? `${Math.round(diffH)}h` : `${Math.round(diffH / 24)}d`;
        return `${d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · ${rel === "in" ? "in" : ""} ${label} ${rel === "ago" ? "ago" : ""}`.trim();
    } catch { return iso; }
}

/**
 * MainDashboard — Tronox I/O top-level dashboard.
 *
 * Shows ONLY active/current operations (outbound export + inbound deliveries)
 * + upcoming GTRB lineup vessels. Everything else is on the Inbound / Outbound
 * silo sub-tabs.
 */
export default function MainDashboard({ onOpenInbound, onOpenOutbound }) {
    const { user } = useAuth();
    const [activeExports, setActiveExports] = useState([]);
    const [upcoming, setUpcoming] = useState([]);
    const [activeDeliveries, setActiveDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openExportId, setOpenExportId] = useState(null);
    const [showStart, setShowStart] = useState(false);
    const pollRef = useRef(null);

    const canOperate = ["admin", "supervisor"].includes(user?.role);

    const load = async () => {
        try {
            const [aRes, uRes, dRes] = await Promise.all([
                api.get("/tronox/exports/active"),
                api.get("/tronox/upcoming-vessels"),
                api.get("/tronox/deliveries/open"),
            ]);
            setActiveExports(aRes.data || []);
            setUpcoming(uRes.data || []);
            setActiveDeliveries(dRes.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    useEffect(() => {
        load();
        pollRef.current = setInterval(load, 8000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    if (openExportId) {
        return <LoadOutStation exportId={openExportId} onBack={() => { setOpenExportId(null); load(); }} />;
    }

    const upcomingOnly = upcoming.filter((v) => v._state === "upcoming");

    return (
        <div className="rounded-xl bg-slate-950 text-slate-100 p-4 sm:p-5 shadow-inner" data-testid="tronox-main-dashboard">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-800">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-400">Tronox · GTRB Terminal</div>
                    <div className="text-2xl font-black tracking-tight">Inbound / Outbound Dashboard</div>
                    <div className="text-xs text-slate-500 mt-0.5">Shows only currently active operations. Use the sub-tabs below to see all silos.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={onOpenInbound}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold"
                        data-testid="open-inbound-silos"
                    >
                        <Truck size={16} weight="bold" /> View All Inbound Silos
                    </button>
                    <button
                        onClick={onOpenOutbound}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-sky-700 hover:bg-sky-600 text-white text-sm font-bold"
                        data-testid="open-outbound-silos"
                    >
                        <Boat size={16} weight="bold" /> View All Outbound Silos
                    </button>
                    {canOperate && (
                        <button
                            onClick={() => setShowStart(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-black"
                            data-testid="start-vessel-btn"
                        >
                            <Plus size={16} weight="bold" /> Start Vessel
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-400 py-10" data-testid="main-dashboard-loading">Loading…</div>
            ) : (
                <div className="mt-4 space-y-6">
                    {/* ═══ ACTIVE OPERATIONS ═══ */}
                    <section>
                        <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-2 flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live now
                        </div>
                        {activeExports.length === 0 && activeDeliveries.length === 0 && (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-center text-slate-500 text-sm" data-testid="no-active-ops">
                                No active inbound deliveries or outbound exports right now.
                            </div>
                        )}

                        {/* Active outbound exports */}
                        {activeExports.map((exp) => (
                            <div
                                key={exp.id}
                                className="rounded-xl border border-sky-500/40 bg-gradient-to-r from-sky-950 to-slate-900 p-4 flex flex-wrap items-center justify-between gap-3 mb-2 cursor-pointer hover:border-sky-400 transition-colors"
                                onClick={() => setOpenExportId(exp.id)}
                                data-testid={`active-export-${exp.id}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-sky-500/20 border border-sky-500/50 flex items-center justify-center">
                                        <Boat size={20} className="text-sky-300" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Outbound export · Load-Out Station</div>
                                        <div className="text-lg font-black">{exp.vessel_name}</div>
                                        <div className="text-xs text-slate-400">
                                            {exp.client_name} · {exp.product} · From <b className="text-teal-300">Silo {exp.current_silo_no}</b>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center">
                                    <div>
                                        <div className="text-[10px] uppercase text-slate-400">Tons loaded</div>
                                        <div className="text-lg font-black text-emerald-300" data-testid={`active-export-tons-${exp.id}`}>{NUM(exp.metrics?.total_tons)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase text-slate-400">Skips</div>
                                        <div className="text-lg font-black text-sky-300">{NUM(exp.metrics?.total_skips, 0)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase text-slate-400">Flow rate</div>
                                        <div className="text-lg font-black text-amber-300">{NUM(exp.metrics?.flow_rate_tph, 1)} t/h</div>
                                    </div>
                                </div>
                                <ArrowRight size={22} className="text-sky-300" />
                            </div>
                        ))}

                        {/* Active inbound deliveries — abbreviated cards */}
                        {activeDeliveries.length > 0 && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="active-inbound-deliveries">
                                <div className="col-span-full text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Inbound deliveries in progress</div>
                                {activeDeliveries.map((d) => (
                                    <div
                                        key={d.id}
                                        className="rounded-lg border border-emerald-500/40 bg-slate-900/60 p-3 flex items-center justify-between gap-3"
                                        data-testid={`active-delivery-${d.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Truck size={20} className="text-emerald-300" />
                                            <div>
                                                <div className="text-sm font-black text-slate-100 font-mono">{d.truck_registration}</div>
                                                <div className="text-[10px] text-slate-400">Silo {d.silo_no} · {d.product || ""}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] text-slate-500">On site</div>
                                            <div className="text-sm font-bold text-emerald-300">{NUM(d.elapsed_minutes, 0)} min</div>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-span-full text-[10px] text-slate-500">
                                    Manage inbound deliveries (pause, resume, close, delays) from the <button onClick={onOpenInbound} className="underline text-emerald-300">Inbound Silos</button> tab.
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ═══ UPCOMING VESSELS ═══ */}
                    <section>
                        <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1">
                            <Calendar size={12} /> Upcoming GTRB vessels
                        </div>
                        {upcomingOnly.length === 0 ? (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-center text-slate-500 text-sm" data-testid="no-upcoming">
                                No upcoming GTRB vessels in the lineup. Add one under Berthing Schedule with division = GTRB.
                            </div>
                        ) : (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden">
                                <div className="grid grid-cols-1 divide-y divide-slate-800" data-testid="upcoming-vessels-list">
                                    {upcomingOnly.map((v) => (
                                        <div
                                            key={v.id}
                                            className="p-3 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-900/60 transition-colors"
                                            data-testid={`upcoming-vessel-${v.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                                                    <Boat size={14} className="text-amber-300" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-black">{v.vessel_name}</div>
                                                    <div className="text-[11px] text-slate-400">{v.client_name} · {v.product} · {NUM(v.tonnage)} t</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[10px] uppercase text-slate-400 tracking-wider">ETA · Planned start</div>
                                                <div className="text-sm font-bold text-amber-300" data-testid={`upcoming-eta-${v.id}`}>
                                                    {fmtETA(v.eta || v.start_datetime)}
                                                </div>
                                                {v.auto_status === "delayed" && (
                                                    <div className="text-[10px] text-rose-400 font-bold flex items-center gap-1 justify-end mt-0.5">
                                                        <WarningOctagon size={10} weight="fill" /> Delayed
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            )}

            {showStart && (
                <StartExportModal onClose={() => setShowStart(false)} onSuccess={() => { setShowStart(false); load(); }} />
            )}
        </div>
    );
}
